import { useNavigate } from "react-router-dom";
import { Pencil } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PermissionGate } from "@/components/common/PermissionGate";
import { formatDateTime } from "@/lib/utils";
import type { BillingCycleExpiryMismatch } from "@/types/billing-cycle";

interface ExpiryMismatchTableProps {
  rows: BillingCycleExpiryMismatch[];
}

/**
 * Renders the signed drift the way an operator has to read it: the sign is the diagnosis, not
 * noise. Ahead of the cycle means the machine keeps running past the term that was paid for;
 * behind it means the customer is being cut short.
 */
function DriftCell({ row }: { row: BillingCycleExpiryMismatch }) {
  if (row.driftDays === null || row.resourceExpiresAt === null) {
    return <span className="text-muted-foreground">—</span>;
  }

  // From the timestamps, not the day count: 0 days is the same day at a different time.
  const ahead = new Date(row.resourceExpiresAt).getTime() > new Date(row.cycleEnd).getTime();
  const label = describeDrift(row.driftDays, ahead);

  return (
    <Badge
      variant="outline"
      className={
        ahead
          ? "border-destructive/30 bg-destructive-soft text-destructive-ink"
          : "border-warn/30 bg-warn-soft text-warn-ink"
      }
    >
      {label}
    </Badge>
  );
}

/** The badge text: whole days apart, or which side is later when both fall on the same day. */
function describeDrift(days: number, ahead: boolean): string {
  if (days === 0) {
    return ahead ? "Same day, later than cycle" : "Same day, earlier than cycle";
  }

  const magnitude = Math.abs(days);
  const unit = magnitude === 1 ? "day" : "days";
  if (ahead) {
    return `+${magnitude} ${unit} past cycle`;
  }

  return `-${magnitude} ${unit} before cycle`;
}

/**
 * The expiry-drift report: machines whose own expiry disagrees with their active billing cycle, or
 * that have no expiry at all — broken data the reconciliation fills in with the cycle end.
 *
 * LIVE machines only. Cycles left open on a deleted machine used to be listed here too, and were
 * dropped — an operator cannot act on a machine that is gone, so those rows only buried the drift
 * that is actually repairable.
 *
 * Both dates are shown side by side rather than just the difference, because which one is right is
 * a judgement the operator makes: the cycle is the record a confirmed payment produced, but a
 * manual grant lives on the resource. Edit opens that machine's expiry screen carrying this exact
 * cycle, where the two can be set to agree.
 */
export function ExpiryMismatchTable({ rows }: ExpiryMismatchTableProps) {
  const navigate = useNavigate();

  return (
    <div className="min-h-0 flex-1 overflow-auto rounded-md border">
      <Table>
        <TableHeader className="sticky top-0 z-30 bg-card">
          <TableRow>
            <TableHead className="text-center">STT</TableHead>
            <TableHead>VM Name</TableHead>
            <TableHead>VM UUID</TableHead>
            <TableHead>User</TableHead>
            <TableHead>Cycle end</TableHead>
            <TableHead>Resource expiry</TableHead>
            <TableHead>Drift</TableHead>
            <TableHead className="sticky right-0 z-20 bg-card text-center shadow-[inset_1px_0_0_0_var(--border)]">
              Actions
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={row.billingCycleId}>
              <TableCell className="text-center text-muted-foreground">
                {index + 1}
              </TableCell>
              <TableCell>
                <div className="max-w-56 truncate font-medium">
                  {row.resourceName}
                </div>
                <div className="text-xs text-muted-foreground">
                  {row.resourceStatus}
                </div>
              </TableCell>

              {/* Shown whole, not truncated: this is the id an operator pastes into WiService or a
                  ticket, and half of one is no use for either. `whitespace-nowrap` keeps it on one
                  line and lets the table scroll rather than wrapping it mid-id. */}
              <TableCell className="whitespace-nowrap font-mono text-xs">
                {row.providerResourceId ?? (
                  <span className="font-sans text-muted-foreground">—</span>
                )}
              </TableCell>
              {/* Name, email and phone, the same three lines the payments queue shows: this is a
                  worklist, and the operator who picks a row up calls the customer about it. */}
              <TableCell className="max-w-56">
                <div className="truncate font-medium">
                  {row.userName || "—"}
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {row.userEmail || "—"}
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {row.userPhone || "—"}
                </div>
              </TableCell>
              <TableCell className="whitespace-nowrap">
                {formatDateTime(row.cycleEnd)}
              </TableCell>
              <TableCell className="whitespace-nowrap">
                {row.resourceExpiresAt ? (
                  formatDateTime(row.resourceExpiresAt)
                ) : (
                  <Badge
                    variant="outline"
                    className="border-destructive/30 bg-destructive-soft text-destructive-ink"
                  >
                    No expiry date
                  </Badge>
                )}
              </TableCell>
              <TableCell>
                <DriftCell row={row} />
              </TableCell>
              <TableCell className="sticky right-0 z-10 bg-card shadow-[inset_1px_0_0_0_var(--border)]">
                <div className="flex items-center justify-center">
                  {/* The report is readable by BillingAdmin and SupportAdmin, but the screen behind
                      Edit is SuperAdmin-only and so are both saves on it. Showing the button to
                      the others would send them into a 403 route. */}
                  <PermissionGate roles="SuperAdmin">
                    <Button
                      variant="outline"
                      size="sm"
                      className="cursor-pointer gap-1"
                      // Carries the cycle id: the machine may have older cycles, and the screen
                      // must land on the one that is actually out of step.
                      onClick={() =>
                        navigate(
                          `/admin/resources/${row.resourceId}/expiry?cycleId=${row.billingCycleId}`,
                        )
                      }
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Edit
                    </Button>
                  </PermissionGate>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
